// src/ui/SampleEditorDialog.tsx
import { useCallback as useCallback5, useState as useState5, useEffect as useEffect4, useRef as useRef3 } from "react";
import * as Dialog from "@radix-ui/react-dialog";

// src/ui/utils.ts
import { cn } from "@audiocontrol/editor-core";

// src/ui/WaveformDisplay.tsx
import { useRef as useRef2, useEffect as useEffect2, useCallback as useCallback2, useState as useState2 } from "react";

// src/ui/SplitWaveformPane.tsx
import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var WAVEFORM_COLOR = "#4a9eff";
var PREVIEW_WAVEFORM_COLOR = "#f0a030";
var BG_COLOR_LEFT = "#1a1a2e";
var BG_COLOR_RIGHT = "#1e1e32";
var CENTER_LINE_COLOR = "#333355";
var HANDLE_COLOR = "#f0a030";
function SplitWaveformPane({
  samples,
  sampleRate: _sampleRate,
  trimRegion,
  onTrimRegionChange,
  zoom,
  height = 200,
  className,
  isPreview = false
}) {
  const containerRef = useRef(null);
  const leftCanvasRef = useRef(null);
  const rightCanvasRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [isDragging, setIsDragging] = useState(null);
  const dragStartX = useRef(0);
  const dragStartValue = useRef(0);
  const windowSamples = useMemo(
    () => Math.round(samples.length / zoom),
    [samples.length, zoom]
  );
  const paneWidth = useMemo(
    () => Math.floor(containerWidth / 2),
    [containerWidth]
  );
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.floor(entry.contentRect.width));
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const drawPane = useCallback(
    (canvas, centerSample, side) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      const midY = h / 2;
      ctx.fillStyle = side === "left" ? BG_COLOR_LEFT : BG_COLOR_RIGHT;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = CENTER_LINE_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(w, midY);
      ctx.stroke();
      const halfWindow = Math.floor(windowSamples / 2);
      const rangeStart = Math.max(0, centerSample - halfWindow);
      const rangeEnd = Math.min(samples.length, centerSample + halfWindow);
      const samplesPerPixel = windowSamples / w;
      ctx.beginPath();
      ctx.strokeStyle = isPreview ? PREVIEW_WAVEFORM_COLOR : WAVEFORM_COLOR;
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x++) {
        const sampleOffset = centerSample - halfWindow + x * samplesPerPixel;
        const sIdx = Math.floor(sampleOffset);
        const sEnd = Math.min(Math.floor(sampleOffset + samplesPerPixel), samples.length - 1);
        if (sIdx < 0 || sIdx >= samples.length) continue;
        let min = 0;
        let max = 0;
        for (let i = Math.max(0, sIdx); i <= Math.min(sEnd, samples.length - 1); i++) {
          if (samples[i] < min) min = samples[i];
          if (samples[i] > max) max = samples[i];
        }
        const yMin = midY - max / 32768 * (midY - 2);
        const yMax = midY - min / 32768 * (midY - 2);
        ctx.moveTo(x + 0.5, yMin);
        ctx.lineTo(x + 0.5, yMax);
      }
      ctx.stroke();
      ctx.strokeStyle = HANDLE_COLOR;
      ctx.lineWidth = 2;
      ctx.beginPath();
      const centerX = Math.floor(w / 2) + 0.5;
      ctx.moveTo(centerX, 0);
      ctx.lineTo(centerX, h);
      ctx.stroke();
      ctx.fillStyle = HANDLE_COLOR;
      ctx.font = "10px monospace";
      const label = side === "left" ? `Start: ${trimRegion.start}` : `End: ${trimRegion.end}`;
      const labelX = side === "left" ? 4 : w - 80;
      ctx.fillText(label, labelX, 12);
    },
    [samples, windowSamples, isPreview, trimRegion.start, trimRegion.end]
  );
  useEffect(() => {
    const leftCanvas = leftCanvasRef.current;
    const rightCanvas = rightCanvasRef.current;
    if (leftCanvas) drawPane(leftCanvas, trimRegion.start, "left");
    if (rightCanvas) drawPane(rightCanvas, trimRegion.end, "right");
  }, [drawPane, trimRegion.start, trimRegion.end, paneWidth, height]);
  const pixelToSampleOffset = useCallback(
    (pixelDelta) => {
      if (paneWidth === 0) return 0;
      return Math.round(pixelDelta * (windowSamples / paneWidth));
    },
    [paneWidth, windowSamples]
  );
  const handleMouseDown = useCallback(
    (target, e) => {
      if (!target) return;
      setIsDragging(target);
      dragStartX.current = e.clientX;
      dragStartValue.current = target === "start" ? trimRegion.start : trimRegion.end;
      e.preventDefault();
    },
    [trimRegion]
  );
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStartX.current;
      const deltaSamples = pixelToSampleOffset(deltaX);
      const newValue = dragStartValue.current + deltaSamples;
      const region = { ...trimRegion };
      if (isDragging === "start") {
        region.start = Math.max(0, Math.min(newValue, region.end - 1));
      } else {
        region.end = Math.max(region.start + 1, Math.min(newValue, samples.length));
      }
      onTrimRegionChange(region);
    },
    [isDragging, pixelToSampleOffset, trimRegion, samples.length, onTrimRegionChange]
  );
  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  return /* @__PURE__ */ jsx("div", { ref: containerRef, className: cn("w-full rounded overflow-hidden", className), children: /* @__PURE__ */ jsxs("div", { className: "flex w-full", children: [
    /* @__PURE__ */ jsx(
      "canvas",
      {
        ref: leftCanvasRef,
        width: paneWidth,
        height,
        style: { width: "50%", height },
        className: cn(
          "rounded-l cursor-ew-resize",
          isDragging === "start" && "ring-2 ring-amber-400"
        ),
        onMouseDown: (e) => handleMouseDown("start", e)
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "w-[2px] bg-gray-600 shrink-0" }),
    /* @__PURE__ */ jsx(
      "canvas",
      {
        ref: rightCanvasRef,
        width: paneWidth,
        height,
        style: { width: "50%", height },
        className: cn(
          "rounded-r cursor-ew-resize",
          isDragging === "end" && "ring-2 ring-amber-400"
        ),
        onMouseDown: (e) => handleMouseDown("end", e)
      }
    )
  ] }) });
}

// src/ui/WaveformDisplay.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var WAVEFORM_COLOR2 = "#4a9eff";
var PREVIEW_WAVEFORM_COLOR2 = "#f0a030";
var SELECTION_COLOR = "rgba(74, 158, 255, 0.25)";
var BG_COLOR = "#1a1a2e";
var CENTER_LINE_COLOR2 = "#333355";
var TRIM_OVERLAY_COLOR = "rgba(0, 0, 0, 0.55)";
var TRIM_HANDLE_COLOR = "#f0a030";
var HANDLE_HIT_PX = 6;
function WaveformDisplay({
  samples,
  sampleRate,
  selection,
  onSelectionChange,
  trimRegion,
  onTrimRegionChange,
  height = 200,
  className,
  isPreview = false,
  zoom = 1
}) {
  const canvasRef = useRef2(null);
  const containerRef = useRef2(null);
  const [canvasWidth, setCanvasWidth] = useState2(800);
  const selectionDragStart = useRef2(null);
  const trimDragTarget = useRef2(null);
  const trimDragRegion = useRef2(null);
  const [cursorStyle, setCursorStyle] = useState2("crosshair");
  const hasTrimHandles = trimRegion != null && onTrimRegionChange != null;
  const useSplitPane = zoom > 1 && hasTrimHandles && trimRegion != null && samples != null && samples.length > 0;
  useEffect2(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanvasWidth(Math.floor(entry.contentRect.width));
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [useSplitPane]);
  useEffect2(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = CENTER_LINE_COLOR2;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();
    if (!samples || samples.length === 0) return;
    if (!hasTrimHandles && selection) {
      const x0 = selection.start / samples.length * w;
      const x1 = selection.end / samples.length * w;
      ctx.fillStyle = SELECTION_COLOR;
      ctx.fillRect(x0, 0, x1 - x0, h);
    }
    drawWaveform(ctx, samples, w, h, isPreview);
    if (hasTrimHandles && trimRegion) {
      const xStart = trimRegion.start / samples.length * w;
      const xEnd = trimRegion.end / samples.length * w;
      ctx.fillStyle = TRIM_OVERLAY_COLOR;
      ctx.fillRect(0, 0, xStart, h);
      ctx.fillRect(xEnd, 0, w - xEnd, h);
      ctx.strokeStyle = TRIM_HANDLE_COLOR;
      ctx.lineWidth = 2;
      for (const x of [xStart, xEnd]) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
    }
  }, [samples, canvasWidth, height, selection, isPreview, trimRegion, hasTrimHandles, useSplitPane]);
  const pixelToSample = useCallback2(
    (clientX) => {
      const canvas = canvasRef.current;
      if (!canvas || !samples) return 0;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      return Math.floor(x / rect.width * samples.length);
    },
    [samples]
  );
  const pixelX = useCallback2(
    (clientX) => {
      const canvas = canvasRef.current;
      if (!canvas) return 0;
      const rect = canvas.getBoundingClientRect();
      return clientX - rect.left;
    },
    []
  );
  const sampleToPixelX = useCallback2(
    (sample) => {
      const canvas = canvasRef.current;
      if (!canvas || !samples) return 0;
      return sample / samples.length * canvas.getBoundingClientRect().width;
    },
    [samples]
  );
  const hitTestHandle = useCallback2(
    (clientX) => {
      if (!trimRegion || !samples) return null;
      const px = pixelX(clientX);
      const startPx = sampleToPixelX(trimRegion.start);
      const endPx = sampleToPixelX(trimRegion.end);
      if (Math.abs(px - startPx) <= HANDLE_HIT_PX) return "start";
      if (Math.abs(px - endPx) <= HANDLE_HIT_PX) return "end";
      return null;
    },
    [trimRegion, samples, pixelX, sampleToPixelX]
  );
  const handleMouseDown = useCallback2(
    (e) => {
      if (!samples) return;
      if (hasTrimHandles) {
        const target = hitTestHandle(e.clientX);
        if (target) {
          trimDragTarget.current = target;
          trimDragRegion.current = trimRegion ? { ...trimRegion } : null;
          e.preventDefault();
        }
        return;
      }
      if (onSelectionChange) {
        selectionDragStart.current = pixelToSample(e.clientX);
      }
    },
    [samples, hasTrimHandles, hitTestHandle, trimRegion, onSelectionChange, pixelToSample]
  );
  const handleMouseMove = useCallback2(
    (e) => {
      if (!samples) return;
      if (hasTrimHandles) {
        if (trimDragTarget.current && trimDragRegion.current && onTrimRegionChange) {
          const sample = Math.max(0, Math.min(samples.length, pixelToSample(e.clientX)));
          const region = { ...trimDragRegion.current };
          if (trimDragTarget.current === "start") {
            region.start = Math.min(sample, region.end - 1);
          } else {
            region.end = Math.max(sample, region.start + 1);
          }
          trimDragRegion.current = region;
          onTrimRegionChange(region);
          return;
        }
        const target = hitTestHandle(e.clientX);
        setCursorStyle(target ? "col-resize" : "default");
        return;
      }
      if (selectionDragStart.current === null || !onSelectionChange) return;
      const current = pixelToSample(e.clientX);
      const start = Math.max(0, Math.min(selectionDragStart.current, current));
      const end = Math.min(samples.length, Math.max(selectionDragStart.current, current));
      onSelectionChange({ start, end });
    },
    [samples, hasTrimHandles, onTrimRegionChange, hitTestHandle, onSelectionChange, pixelToSample]
  );
  const handleMouseUp = useCallback2(() => {
    trimDragTarget.current = null;
    trimDragRegion.current = null;
    selectionDragStart.current = null;
  }, []);
  if (useSplitPane) {
    return /* @__PURE__ */ jsx2(
      SplitWaveformPane,
      {
        samples,
        sampleRate,
        trimRegion,
        onTrimRegionChange,
        zoom,
        height,
        className,
        isPreview
      }
    );
  }
  return /* @__PURE__ */ jsx2("div", { ref: containerRef, className: cn("w-full rounded overflow-hidden", className), children: /* @__PURE__ */ jsx2(
    "canvas",
    {
      ref: canvasRef,
      width: canvasWidth,
      height,
      className: "w-full",
      style: { cursor: cursorStyle },
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp
    }
  ) });
}
function drawWaveform(ctx, samples, w, h, isPreview) {
  const samplesPerPixel = samples.length / w;
  ctx.strokeStyle = isPreview ? PREVIEW_WAVEFORM_COLOR2 : WAVEFORM_COLOR2;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 0; x < w; x++) {
    const start = Math.floor(x * samplesPerPixel);
    const end = Math.min(Math.floor((x + 1) * samplesPerPixel), samples.length);
    let min = 0;
    let max = 0;
    for (let i = start; i < end; i++) {
      if (samples[i] < min) min = samples[i];
      if (samples[i] > max) max = samples[i];
    }
    const yMin = (1 - max / 32768) * h / 2;
    const yMax = (1 - min / 32768) * h / 2;
    ctx.moveTo(x + 0.5, yMin);
    ctx.lineTo(x + 0.5, yMax);
  }
  ctx.stroke();
}

// src/ui/OperationPanel.tsx
import { useState as useState3, useCallback as useCallback3, useEffect as useEffect3 } from "react";
import * as Tabs from "@radix-ui/react-tabs";

// src/operations/trim.ts
function trimSamples(samples, startSample, endSample) {
  const start = Math.max(0, Math.floor(startSample));
  const end = Math.min(samples.length, Math.floor(endSample));
  if (start >= end) {
    return new Int16Array(0);
  }
  return samples.slice(start, end);
}
function findTrimPoints(samples, sampleRate, thresholdDb) {
  if (samples.length === 0) {
    return { start: 0, end: 0 };
  }
  const windowSize = Math.max(1, Math.floor(sampleRate * 0.01));
  const thresholdLinear = Math.pow(10, thresholdDb / 20);
  const thresholdRmsSquared = thresholdLinear * thresholdLinear;
  const scale = 1 / 32768;
  let firstAbove = samples.length;
  for (let i = 0; i <= samples.length - windowSize; i += windowSize) {
    const rmsSquared = computeRmsSquared(samples, i, windowSize, scale);
    if (rmsSquared > thresholdRmsSquared) {
      firstAbove = i;
      break;
    }
  }
  if (firstAbove >= samples.length) {
    return { start: 0, end: 0 };
  }
  let lastAbove = 0;
  for (let i = samples.length - windowSize; i >= 0; i -= windowSize) {
    const rmsSquared = computeRmsSquared(samples, i, windowSize, scale);
    if (rmsSquared > thresholdRmsSquared) {
      lastAbove = Math.min(i + windowSize, samples.length);
      break;
    }
  }
  if (firstAbove >= lastAbove) {
    return { start: 0, end: 0 };
  }
  return { start: firstAbove, end: lastAbove };
}
function computeRmsSquared(samples, offset, windowSize, scale) {
  let sumSquared = 0;
  for (let j = 0; j < windowSize; j++) {
    const normalized = samples[offset + j] * scale;
    sumSquared += normalized * normalized;
  }
  return sumSquared / windowSize;
}

// src/operations/normalize.ts
function normalize(samples, targetPeakDb = 0) {
  if (samples.length === 0) {
    return new Int16Array(0);
  }
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const abs = Math.abs(samples[i]);
    if (abs > peak) {
      peak = abs;
    }
  }
  if (peak === 0) {
    return new Int16Array(samples);
  }
  const targetLinear = Math.pow(10, targetPeakDb / 20) * 32767;
  const scaleFactor = targetLinear / peak;
  return applyScaleFactor(samples, scaleFactor);
}
function applyGain(samples, gainDb) {
  if (samples.length === 0) {
    return new Int16Array(0);
  }
  const scaleFactor = Math.pow(10, gainDb / 20);
  return applyScaleFactor(samples, scaleFactor);
}
function applyScaleFactor(samples, scaleFactor) {
  const result = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const scaled = Math.round(samples[i] * scaleFactor);
    result[i] = Math.max(-32768, Math.min(32767, scaled));
  }
  return result;
}

// src/operations/fade.ts
function fadeIn(samples, sampleRate, durationMs, curve = "linear") {
  const fadeSamples = Math.min(
    Math.floor(durationMs / 1e3 * sampleRate),
    samples.length
  );
  const result = new Int16Array(samples);
  const curveFunction = getCurveFunction(curve);
  for (let i = 0; i < fadeSamples; i++) {
    const t = i / fadeSamples;
    result[i] = Math.round(samples[i] * curveFunction(t));
  }
  return result;
}
function fadeOut(samples, sampleRate, durationMs, curve = "linear") {
  const fadeSamples = Math.min(
    Math.floor(durationMs / 1e3 * sampleRate),
    samples.length
  );
  const result = new Int16Array(samples);
  const curveFunction = getCurveFunction(curve);
  const fadeStart = samples.length - fadeSamples;
  for (let i = 0; i < fadeSamples; i++) {
    const t = fadeSamples > 1 ? (fadeSamples - 1 - i) / (fadeSamples - 1) : 0;
    result[fadeStart + i] = Math.round(samples[fadeStart + i] * curveFunction(t));
  }
  return result;
}
function getCurveFunction(curve) {
  switch (curve) {
    case "linear":
      return (t) => t;
    case "equal-power":
      return (t) => Math.sin(t * Math.PI / 2);
    case "logarithmic":
      return (t) => Math.log10(1 + 9 * t);
  }
}

// src/operations/reverse.ts
function reverseSamples(samples) {
  const result = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    result[i] = samples[samples.length - 1 - i];
  }
  return result;
}

// src/ui/OperationPanel.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var TAB_LABELS = {
  trim: "Trim",
  normalize: "Normalize",
  gain: "Gain",
  fade: "Fade",
  reverse: "Reverse"
};
var TABS = ["trim", "normalize", "gain", "fade", "reverse"];
var SLIDER_CLASS = "w-full accent-ac-highlight";
var APPLY_BTN = "ac-btn ac-btn-sm ac-btn-primary font-medium";
var APPLY_BTN_INACTIVE = "ac-btn ac-btn-sm opacity-50 cursor-not-allowed";
function OperationPanel({
  samples,
  sampleRate,
  selection,
  trimRegion,
  onTrimRegionChange,
  onPreview,
  onCommit,
  onApply,
  hasPreview
}) {
  const [activeTab, setActiveTab] = useState3("trim");
  const [silenceThresholdDb, setSilenceThresholdDb] = useState3(-40);
  const [targetPeakDb, setTargetPeakDb] = useState3(0);
  const [gainDb, setGainDb] = useState3(0);
  const [fadeDurationMs, setFadeDurationMs] = useState3(50);
  const [fadeCurve, setFadeCurve] = useState3("linear");
  const [fadeDirection, setFadeDirection] = useState3("in");
  const disabled = !samples || samples.length === 0;
  const handleTabChange = useCallback3(
    (value) => {
      onPreview(null);
      onTrimRegionChange(null);
      const tab = value;
      setActiveTab(tab);
      if (tab === "trim" && samples && samples.length > 0) {
        onTrimRegionChange({ start: 0, end: samples.length });
      }
    },
    [onPreview, onTrimRegionChange, samples]
  );
  useEffect3(() => {
    if (activeTab === "trim" && samples && samples.length > 0 && !trimRegion) {
      onTrimRegionChange({ start: 0, end: samples.length });
    }
  }, [activeTab, samples, trimRegion, onTrimRegionChange]);
  useEffect3(() => {
    if (disabled || activeTab !== "normalize") return;
    const peak = targetPeakDb;
    onPreview((s) => normalize(s, peak));
  }, [activeTab, targetPeakDb, disabled, onPreview]);
  useEffect3(() => {
    if (disabled || activeTab !== "gain") return;
    const g = gainDb;
    onPreview((s) => applyGain(s, g));
  }, [activeTab, gainDb, disabled, onPreview]);
  useEffect3(() => {
    if (disabled || activeTab !== "fade") return;
    const ms = fadeDurationMs;
    const curve = fadeCurve;
    const dir = fadeDirection;
    onPreview(
      (s, sr) => dir === "in" ? fadeIn(s, sr, ms, curve) : fadeOut(s, sr, ms, curve)
    );
  }, [activeTab, fadeDurationMs, fadeCurve, fadeDirection, disabled, onPreview]);
  useEffect3(() => {
    if (disabled || activeTab !== "trim" || !samples) return;
    const points = findTrimPoints(samples, sampleRate, silenceThresholdDb);
    if (points.start >= points.end) {
      onTrimRegionChange({ start: 0, end: samples.length });
    } else {
      onTrimRegionChange(points);
    }
  }, [activeTab, silenceThresholdDb, disabled, samples, sampleRate, onTrimRegionChange]);
  useEffect3(() => {
    if (disabled || activeTab !== "trim" || !trimRegion || !samples) return;
    const { start, end } = trimRegion;
    if (start === 0 && end === samples.length) {
      onPreview(null);
    } else {
      onPreview((s) => trimSamples(s, start, end));
    }
  }, [activeTab, trimRegion, disabled, samples, onPreview]);
  return /* @__PURE__ */ jsxs2(Tabs.Root, { value: activeTab, onValueChange: handleTabChange, children: [
    /* @__PURE__ */ jsx3(Tabs.List, { className: "flex border-b border-ac-accent/30 mb-4", children: TABS.map((tab) => /* @__PURE__ */ jsx3(
      Tabs.Trigger,
      {
        value: tab,
        className: cn(
          "px-4 py-2 text-sm border-b-2 -mb-px transition-colors",
          activeTab === tab ? "border-ac-highlight text-ac-text" : "border-transparent text-ac-muted hover:text-ac-text"
        ),
        children: TAB_LABELS[tab]
      },
      tab
    )) }),
    /* @__PURE__ */ jsx3(
      TrimTab,
      {
        disabled,
        samples,
        sampleRate,
        trimRegion,
        hasPreview,
        silenceThresholdDb,
        onSilenceThresholdChange: setSilenceThresholdDb,
        onApplyTrim: () => {
          if (!trimRegion) return;
          const { start, end } = trimRegion;
          onApply("Trim", (s) => trimSamples(s, start, end));
          onTrimRegionChange(null);
        },
        onResetTrim: () => {
          if (samples) {
            onTrimRegionChange({ start: 0, end: samples.length });
          }
        }
      }
    ),
    /* @__PURE__ */ jsx3(
      NormalizeTab,
      {
        disabled,
        hasPreview,
        targetPeakDb,
        onTargetPeakChange: setTargetPeakDb,
        onCommit: () => onCommit(`Normalize (${targetPeakDb} dB)`)
      }
    ),
    /* @__PURE__ */ jsx3(
      GainTab,
      {
        disabled,
        hasPreview,
        gainDb,
        onGainChange: setGainDb,
        onCommit: () => onCommit(`Gain ${gainDb >= 0 ? "+" : ""}${gainDb} dB`)
      }
    ),
    /* @__PURE__ */ jsx3(
      FadeTab,
      {
        disabled,
        hasPreview,
        fadeDurationMs,
        onFadeDurationChange: setFadeDurationMs,
        fadeCurve,
        onFadeCurveChange: setFadeCurve,
        fadeDirection,
        onFadeDirectionChange: setFadeDirection,
        onCommit: () => {
          const dir = fadeDirection === "in" ? "In" : "Out";
          onCommit(`Fade ${dir} (${fadeDurationMs}ms, ${fadeCurve})`);
        }
      }
    ),
    /* @__PURE__ */ jsxs2(Tabs.Content, { value: "reverse", className: "space-y-3", children: [
      /* @__PURE__ */ jsx3("p", { className: "text-xs text-ac-muted", children: "Reverse the entire sample." }),
      /* @__PURE__ */ jsx3(
        "button",
        {
          className: "ac-btn ac-btn-sm",
          disabled,
          onClick: () => onApply("Reverse", (s) => reverseSamples(s)),
          children: "Reverse"
        }
      )
    ] })
  ] });
}
function TrimTab({
  disabled,
  samples,
  sampleRate,
  trimRegion,
  hasPreview,
  silenceThresholdDb,
  onSilenceThresholdChange,
  onApplyTrim,
  onResetTrim
}) {
  const totalSamples = samples?.length ?? 0;
  const keepStart = trimRegion?.start ?? 0;
  const keepEnd = trimRegion?.end ?? totalSamples;
  const keepCount = keepEnd - keepStart;
  const removedCount = totalSamples - keepCount;
  const keepMs = sampleRate > 0 ? (keepCount / sampleRate * 1e3).toFixed(1) : "0.0";
  const removedMs = sampleRate > 0 ? (removedCount / sampleRate * 1e3).toFixed(1) : "0.0";
  return /* @__PURE__ */ jsxs2(Tabs.Content, { value: "trim", className: "space-y-3", children: [
    /* @__PURE__ */ jsx3("p", { className: "text-xs text-ac-muted", children: "Drag the handles on the waveform to adjust the trim region, or use the silence threshold to auto-detect." }),
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs2("label", { className: "flex items-center justify-between text-xs text-ac-muted", children: [
        /* @__PURE__ */ jsx3("span", { children: "Silence Threshold" }),
        /* @__PURE__ */ jsxs2("span", { className: "font-mono", children: [
          silenceThresholdDb,
          " dB"
        ] })
      ] }),
      /* @__PURE__ */ jsx3(
        "input",
        {
          type: "range",
          className: SLIDER_CLASS,
          min: -60,
          max: -10,
          step: 1,
          value: silenceThresholdDb,
          onChange: (e) => onSilenceThresholdChange(Number(e.target.value))
        }
      )
    ] }),
    trimRegion && totalSamples > 0 && /* @__PURE__ */ jsxs2("div", { className: "text-xs text-ac-muted font-mono", children: [
      "Keep ",
      keepStart,
      " \u2192 ",
      keepEnd,
      " (",
      keepMs,
      "ms, removing ",
      removedMs,
      "ms)"
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx3(
        "button",
        {
          className: hasPreview ? APPLY_BTN : APPLY_BTN_INACTIVE,
          disabled: disabled || !hasPreview,
          onClick: onApplyTrim,
          children: "Apply Trim"
        }
      ),
      /* @__PURE__ */ jsx3(
        "button",
        {
          className: "ac-btn ac-btn-sm",
          disabled,
          onClick: onResetTrim,
          children: "Reset"
        }
      )
    ] })
  ] });
}
function NormalizeTab({
  disabled,
  hasPreview,
  targetPeakDb,
  onTargetPeakChange,
  onCommit
}) {
  return /* @__PURE__ */ jsxs2(Tabs.Content, { value: "normalize", className: "space-y-3", children: [
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs2("label", { className: "flex items-center justify-between text-xs text-ac-muted", children: [
        /* @__PURE__ */ jsx3("span", { children: "Target Peak" }),
        /* @__PURE__ */ jsxs2("span", { className: "font-mono", children: [
          targetPeakDb,
          " dB"
        ] })
      ] }),
      /* @__PURE__ */ jsx3(
        "input",
        {
          type: "range",
          className: SLIDER_CLASS,
          min: -12,
          max: 0,
          step: 0.5,
          value: targetPeakDb,
          onChange: (e) => onTargetPeakChange(Number(e.target.value))
        }
      )
    ] }),
    /* @__PURE__ */ jsx3(
      "button",
      {
        className: hasPreview ? APPLY_BTN : APPLY_BTN_INACTIVE,
        disabled: disabled || !hasPreview,
        onClick: onCommit,
        children: "Apply"
      }
    )
  ] });
}
function GainTab({
  disabled,
  hasPreview,
  gainDb,
  onGainChange,
  onCommit
}) {
  return /* @__PURE__ */ jsxs2(Tabs.Content, { value: "gain", className: "space-y-3", children: [
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs2("label", { className: "flex items-center justify-between text-xs text-ac-muted", children: [
        /* @__PURE__ */ jsx3("span", { children: "Gain" }),
        /* @__PURE__ */ jsxs2("span", { className: "font-mono", children: [
          gainDb >= 0 ? "+" : "",
          gainDb,
          " dB"
        ] })
      ] }),
      /* @__PURE__ */ jsx3(
        "input",
        {
          type: "range",
          className: SLIDER_CLASS,
          min: -20,
          max: 20,
          step: 0.5,
          value: gainDb,
          onChange: (e) => onGainChange(Number(e.target.value))
        }
      )
    ] }),
    /* @__PURE__ */ jsx3(
      "button",
      {
        className: hasPreview ? APPLY_BTN : APPLY_BTN_INACTIVE,
        disabled: disabled || !hasPreview,
        onClick: onCommit,
        children: "Apply"
      }
    )
  ] });
}
function FadeTab({
  disabled,
  hasPreview,
  fadeDurationMs,
  onFadeDurationChange,
  fadeCurve,
  onFadeCurveChange,
  fadeDirection,
  onFadeDirectionChange,
  onCommit
}) {
  return /* @__PURE__ */ jsxs2(Tabs.Content, { value: "fade", className: "space-y-3", children: [
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs2("label", { className: "flex items-center justify-between text-xs text-ac-muted", children: [
        /* @__PURE__ */ jsx3("span", { children: "Duration" }),
        /* @__PURE__ */ jsxs2("span", { className: "font-mono", children: [
          fadeDurationMs,
          " ms"
        ] })
      ] }),
      /* @__PURE__ */ jsx3(
        "input",
        {
          type: "range",
          className: SLIDER_CLASS,
          min: 5,
          max: 2e3,
          step: 5,
          value: fadeDurationMs,
          onChange: (e) => onFadeDurationChange(Number(e.target.value))
        }
      )
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx3("label", { className: "text-xs text-ac-muted", children: "Curve" }),
      /* @__PURE__ */ jsxs2(
        "select",
        {
          className: "w-full bg-ac-panel text-ac-text text-xs rounded px-2 py-1 border border-ac-accent",
          value: fadeCurve,
          onChange: (e) => onFadeCurveChange(e.target.value),
          children: [
            /* @__PURE__ */ jsx3("option", { value: "linear", children: "Linear" }),
            /* @__PURE__ */ jsx3("option", { value: "equal-power", children: "Equal Power" }),
            /* @__PURE__ */ jsx3("option", { value: "logarithmic", children: "Logarithmic" })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx3("div", { className: "flex rounded overflow-hidden border border-ac-accent/50", children: ["in", "out"].map((dir) => /* @__PURE__ */ jsxs2(
      "button",
      {
        onClick: () => onFadeDirectionChange(dir),
        className: cn(
          "flex-1 px-3 py-1.5 text-xs transition-colors",
          fadeDirection === dir ? "bg-ac-highlight text-white" : "bg-ac-bg text-ac-muted hover:text-ac-text"
        ),
        children: [
          "Fade ",
          dir === "in" ? "In" : "Out"
        ]
      },
      dir
    )) }),
    /* @__PURE__ */ jsx3(
      "button",
      {
        className: hasPreview ? APPLY_BTN : APPLY_BTN_INACTIVE,
        disabled: disabled || !hasPreview,
        onClick: onCommit,
        children: "Apply"
      }
    )
  ] });
}

// src/ui/hooks/useSampleEditor.ts
import { useState as useState4, useCallback as useCallback4, useMemo as useMemo2 } from "react";
var MAX_HISTORY = 50;
function useSampleEditor({
  initialSamples,
  sampleRate
}) {
  const [historyState, setHistoryState] = useState4(() => ({
    entries: initialSamples ? [{ label: "Original", samples: initialSamples }] : [],
    currentIndex: initialSamples ? 0 : -1
  }));
  const [selection, setSelection] = useState4(null);
  const [trimRegion, setTrimRegion] = useState4(null);
  const [preview, setPreviewState] = useState4(null);
  const currentEntry = historyState.currentIndex >= 0 ? historyState.entries[historyState.currentIndex] : null;
  const samples = currentEntry?.samples ?? null;
  const apply = useCallback4(
    (label, operation) => {
      setHistoryState((prev) => {
        const current = prev.currentIndex >= 0 ? prev.entries[prev.currentIndex] : null;
        if (!current) {
          return prev;
        }
        const result = operation(current.samples, sampleRate);
        const newEntry = { label, samples: result };
        const base = prev.entries.slice(0, prev.currentIndex + 1);
        const entries = [...base, newEntry];
        if (entries.length > MAX_HISTORY) {
          const excess = entries.length - MAX_HISTORY;
          return {
            entries: entries.slice(excess),
            currentIndex: entries.length - excess - 1
          };
        }
        return {
          entries,
          currentIndex: entries.length - 1
        };
      });
    },
    [sampleRate]
  );
  const setPreview = useCallback4(
    (operation) => {
      if (!operation || !samples) {
        setPreviewState(null);
        return;
      }
      setPreviewState(operation(samples, sampleRate));
    },
    [samples, sampleRate]
  );
  const commitPreview = useCallback4(
    (label) => {
      if (!preview) return;
      const committed = preview;
      setPreviewState(null);
      setHistoryState((prev) => {
        const base = prev.entries.slice(0, prev.currentIndex + 1);
        const entries = [...base, { label, samples: committed }];
        if (entries.length > MAX_HISTORY) {
          const excess = entries.length - MAX_HISTORY;
          return { entries: entries.slice(excess), currentIndex: entries.length - excess - 1 };
        }
        return { entries, currentIndex: entries.length - 1 };
      });
    },
    [preview]
  );
  const displaySamples = preview ?? samples;
  const canUndo = historyState.currentIndex > 0;
  const canRedo = historyState.currentIndex < historyState.entries.length - 1;
  const undo = useCallback4(() => {
    setPreviewState(null);
    setHistoryState(
      (prev) => prev.currentIndex > 0 ? { ...prev, currentIndex: prev.currentIndex - 1 } : prev
    );
  }, []);
  const redo = useCallback4(() => {
    setPreviewState(null);
    setHistoryState(
      (prev) => prev.currentIndex < prev.entries.length - 1 ? { ...prev, currentIndex: prev.currentIndex + 1 } : prev
    );
  }, []);
  const isDirty = samples !== initialSamples;
  const history = useMemo2(
    () => historyState.entries.map((entry, i) => ({
      label: entry.label,
      isCurrent: i === historyState.currentIndex
    })),
    [historyState]
  );
  return {
    samples,
    sampleRate,
    selection,
    setSelection,
    trimRegion,
    setTrimRegion,
    apply,
    canUndo,
    canRedo,
    undo,
    redo,
    isDirty,
    history,
    preview,
    setPreview,
    commitPreview,
    displaySamples
  };
}

// src/ui/SampleEditorDialog.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function SampleEditorDialog({
  open,
  onOpenChange,
  samples: initialSamples,
  sampleRate,
  sampleName,
  onSave
}) {
  const editor = useSampleEditor({ initialSamples, sampleRate });
  const dialogRef = useRef3(null);
  const ZOOM_STEPS = [1, 2, 4, 8, 16, 32, 64];
  const [zoom, setZoom] = useState5(1);
  const zoomIn = useCallback5(() => {
    setZoom((z) => {
      const idx = ZOOM_STEPS.indexOf(z);
      return idx < ZOOM_STEPS.length - 1 ? ZOOM_STEPS[idx + 1] : z;
    });
  }, []);
  const zoomOut = useCallback5(() => {
    setZoom((z) => {
      const idx = ZOOM_STEPS.indexOf(z);
      return idx > 0 ? ZOOM_STEPS[idx - 1] : z;
    });
  }, []);
  const zoomReset = useCallback5(() => {
    setZoom(1);
  }, []);
  useEffect4(() => {
    const handleKeyDown = (e) => {
      if (!dialogRef.current?.contains(e.target)) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case "+":
        case "=":
          zoomIn();
          e.preventDefault();
          break;
        case "-":
          zoomOut();
          e.preventDefault();
          break;
        case "0":
          zoomReset();
          e.preventDefault();
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomIn, zoomOut, zoomReset]);
  const handleSave = useCallback5(() => {
    if (!editor.samples || !onSave) return;
    onSave(editor.samples, editor.sampleRate);
    onOpenChange(false);
  }, [editor.samples, editor.sampleRate, onSave, onOpenChange]);
  const durationSec = editor.samples ? (editor.samples.length / editor.sampleRate).toFixed(3) : "0.000";
  const sampleCount = editor.samples?.length ?? 0;
  return /* @__PURE__ */ jsx4(Dialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs3(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx4(Dialog.Overlay, { className: "fixed inset-0 bg-black/50 z-50" }),
    /* @__PURE__ */ jsxs3(Dialog.Content, { ref: dialogRef, className: "fixed z-50 inset-4 bg-ac-panel border border-ac-accent rounded-lg shadow-xl overflow-hidden flex flex-col", children: [
      /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between p-4 border-b border-ac-accent shrink-0", children: [
        /* @__PURE__ */ jsxs3("div", { children: [
          /* @__PURE__ */ jsx4(Dialog.Title, { className: "text-lg font-bold text-ac-text", children: "Sample Editor" }),
          /* @__PURE__ */ jsx4(Dialog.Description, { className: "text-sm text-ac-muted", children: sampleName })
        ] }),
        /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx4(
            "button",
            {
              className: cn("ac-btn ac-btn-sm", !editor.canUndo && "opacity-50 cursor-not-allowed"),
              disabled: !editor.canUndo,
              onClick: editor.undo,
              title: "Undo",
              children: "Undo"
            }
          ),
          /* @__PURE__ */ jsx4(
            "button",
            {
              className: cn("ac-btn ac-btn-sm", !editor.canRedo && "opacity-50 cursor-not-allowed"),
              disabled: !editor.canRedo,
              onClick: editor.redo,
              title: "Redo",
              children: "Redo"
            }
          ),
          /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-1 border-l border-ac-accent pl-2 ml-1", children: [
            /* @__PURE__ */ jsx4(
              "button",
              {
                className: cn("ac-btn ac-btn-sm", zoom <= 1 && "opacity-50 cursor-not-allowed"),
                disabled: zoom <= 1,
                onClick: zoomOut,
                title: "Zoom out (-)",
                children: "-"
              }
            ),
            /* @__PURE__ */ jsxs3(
              "button",
              {
                className: "ac-btn ac-btn-sm min-w-[40px] text-center",
                onClick: zoomReset,
                title: "Reset zoom (0)",
                children: [
                  zoom,
                  "x"
                ]
              }
            ),
            /* @__PURE__ */ jsx4(
              "button",
              {
                className: cn("ac-btn ac-btn-sm", zoom >= 64 && "opacity-50 cursor-not-allowed"),
                disabled: zoom >= 64,
                onClick: zoomIn,
                title: "Zoom in (+)",
                children: "+"
              }
            )
          ] }),
          onSave && /* @__PURE__ */ jsx4(
            "button",
            {
              className: cn(
                "ac-btn ac-btn-sm ac-btn-primary",
                !editor.isDirty && "opacity-50 cursor-not-allowed"
              ),
              disabled: !editor.isDirty,
              onClick: handleSave,
              children: "Save"
            }
          ),
          /* @__PURE__ */ jsx4(Dialog.Close, { className: "p-2 text-ac-muted hover:text-ac-text transition-colors", children: /* @__PURE__ */ jsx4("svg", { className: "w-5 h-5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx4("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs3("div", { className: "flex-1 overflow-auto p-4 space-y-4", children: [
        /* @__PURE__ */ jsx4(
          WaveformDisplay,
          {
            samples: editor.samples,
            sampleRate: editor.sampleRate,
            selection: editor.selection,
            onSelectionChange: editor.setSelection,
            trimRegion: editor.trimRegion,
            onTrimRegionChange: editor.setTrimRegion,
            height: 220,
            isPreview: editor.preview !== null,
            zoom
          }
        ),
        /* @__PURE__ */ jsx4(
          OperationPanel,
          {
            samples: editor.samples,
            sampleRate: editor.sampleRate,
            selection: editor.selection,
            trimRegion: editor.trimRegion,
            onTrimRegionChange: editor.setTrimRegion,
            onPreview: editor.setPreview,
            onCommit: editor.commitPreview,
            onApply: editor.apply,
            hasPreview: editor.preview !== null
          }
        ),
        editor.history.length > 1 && /* @__PURE__ */ jsxs3("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx4("div", { className: "text-xs font-semibold uppercase tracking-wide text-ac-muted", children: "History" }),
          /* @__PURE__ */ jsx4("div", { className: "flex flex-wrap gap-1", children: editor.history.map((entry, i) => /* @__PURE__ */ jsx4(
            "span",
            {
              className: cn(
                "text-xs px-2 py-0.5 rounded",
                entry.isCurrent ? "bg-ac-highlight/20 text-ac-text" : "text-ac-muted"
              ),
              children: entry.label
            },
            i
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-4 px-4 py-2 border-t border-ac-accent text-xs text-ac-muted shrink-0", children: [
        /* @__PURE__ */ jsxs3("span", { children: [
          durationSec,
          "s"
        ] }),
        /* @__PURE__ */ jsxs3("span", { children: [
          sampleRate,
          " Hz"
        ] }),
        /* @__PURE__ */ jsxs3("span", { children: [
          sampleCount.toLocaleString(),
          " samples"
        ] }),
        editor.preview !== null && /* @__PURE__ */ jsx4("span", { className: "text-amber-400 font-medium", children: "Preview" }),
        editor.isDirty && /* @__PURE__ */ jsx4("span", { className: "text-ac-highlight", children: "Modified" })
      ] })
    ] })
  ] }) });
}
export {
  SampleEditorDialog,
  WaveformDisplay,
  useSampleEditor
};
//# sourceMappingURL=index.js.map